// GitHub-enum mappers — the driver's REST observation builder reuses every one
// of these. See `../babysit.ts` for the file docblock.

import type { ScreenHeldPrField } from '../../value-objects/screen-held-pr-text';
import {
  FINDING_SEVERITY_RANK,
  findingBodyIsBlocking,
  parseFindingSeverity,
} from './finding-severity';
import type {
  CheckGrade,
  FindingSeverity,
  Mergeability,
  PrFinding,
  ReviewState,
} from './observation';

// ─── GitHub-enum mappers (shared by the driver's REST observation builder) ───────
//
// Pure maps from GitHub's enum strings (webhook + REST payloads carry the same
// vocabulary) into the observation types above. The mothership babysit driver's
// `observe` builder reuses these so the on-wire shape and the decision core never
// drift.

/**
 * The known bots by LOGIN, lower-cased (11-11). Several run as plain USER accounts —
 * self-hosted Renovate as `renovate-bot`, Copilot's coding agent as `Copilot` — so
 * neither `type: 'Bot'` nor the `[bot]` suffix names them. `packages/review` imports
 * this one directly. `@developerz/github` `bot-detect.ts` keeps its own copy for the
 * ingest/dispatch reply-loop filter, because `packages/github` may not import domain
 * (`import-boundaries` LAYERS); `bot-identity.test.ts` pins the two tables equal.
 */
export const KNOWN_BOT_LOGINS = {
  coderabbit: ['coderabbitai[bot]', 'coderabbitai'],
  copilot: [
    'copilot-pull-request-reviewer[bot]',
    'copilot-swe-agent[bot]',
    'copilot[bot]',
    'copilot',
  ],
  dependabot: ['dependabot[bot]', 'dependabot-preview[bot]'],
  renovate: ['renovate[bot]', 'renovate-bot'],
} as const satisfies Record<string, readonly string[]>;

const KNOWN_BOT_LOGIN_SET: ReadonlySet<string> = new Set(Object.values(KNOWN_BOT_LOGINS).flat());

/**
 * THE bot predicate of the babysit lane — `@developerz/github` `isBot`'s rule: a
 * `type: 'Bot'` author, a `*[bot]` login, or a {@link KNOWN_BOT_LOGINS} login. Every
 * babysit reader (the observation, the merge egress, `classifyAskBeforeActing`) asks
 * this one, so a bot's comment is never feedback to address.
 */
export function looksLikeBot(author: { login: string; type?: string | undefined }): boolean {
  if (author.type === 'Bot') return true;
  const login = author.login.toLowerCase();
  return login.endsWith('[bot]') || KNOWN_BOT_LOGIN_SET.has(login);
}

/**
 * The ONE ruling on every GitHub review state, normalised — three tiers, because
 * that is what the REST `/reviews` vocabulary actually carries:
 *
 *   - `APPROVED` / `CHANGES_REQUESTED` — a POSITION on the merge, returned as-is.
 *   - `DISMISSED` — a CLEARING action: GitHub rewrites a review's OWN state to
 *     this once it has been dismissed, and the author's prior verdict no longer
 *     stands. Measured on developerz.ai#3663: our APPROVED row, then a later
 *     DISMISSED row, and GitHub's own `latestOpinionatedReviews` came back EMPTY.
 *   - `COMMENTED` is a plain comment, `PENDING` an unsubmitted draft, and
 *     anything else is drift — `null`: no position, and nothing cleared. Reading
 *     a later COMMENT as a latest state is what made a comment CLEAR an
 *     outstanding `CHANGES_REQUESTED`, the one direction that fails open.
 *
 * Caller contract: `'DISMISSED'` must be branched on as DELETE-of-the-author's-
 * entry — the retraction of whatever that author previously filed — never stored
 * as a "latest verdict". `null` skips; a position sets.
 *
 * Here rather than beside any one caller because THREE reductions run over the
 * same REST list and are deliberately different — `reviewGateFrom` keeps each
 * reviewer's latest standing opinion, clearing it when that reviewer's own later
 * row is `DISMISSED` (#4224), `opinionatedReviewsFrom` keeps the standing
 * verdicts per author, and `standingReviewerVerdicts` in `@developerz/tools`
 * feeds the PR tool's review state (#4199). What all three share is only this:
 * which tier each state falls in.
 *
 * Normalising is load-bearing too, not a convenience. Two of those callers
 * uppercased before comparing and one did not, so the same review list could be
 * decisive on one path and invisible on the other.
 */
export function decisiveReviewState(
  state: string,
): 'APPROVED' | 'CHANGES_REQUESTED' | 'DISMISSED' | null {
  const upper = state.toUpperCase();
  return upper === 'APPROVED' || upper === 'CHANGES_REQUESTED' || upper === 'DISMISSED'
    ? upper
    : null;
}

/** Map GitHub's `reviewDecision` to our {@link ReviewState} (null / unknown ⇒ no gate). */
export function mapReviewDecision(decision: string | null | undefined): ReviewState {
  switch ((decision ?? '').toUpperCase()) {
    case 'APPROVED':
      return 'approved';
    case 'CHANGES_REQUESTED':
      return 'changes_requested';
    case 'REVIEW_REQUIRED':
      return 'pending';
    default:
      return 'none';
  }
}

/**
 * Map OUR platform reviewer's recorded verdict (#838 — the `review_posted` status
 * event's `verdict`, the `@developerz/review` `Verdict` vocabulary restated as a
 * string so domain keeps its no-internal-deps charter) onto {@link ReviewState}.
 * `approve` ⇒ `approved`, `request_changes` ⇒ `changes_requested`, and everything
 * else — a `comment` verdict (non-blocking findings assert no gate position), an
 * unknown value, or no recorded verdict — ⇒ `none`. Pure; the observe seam does
 * the DB read + head pinning, this maps the shape.
 */
export function mapPlatformReviewVerdict(verdict: string | null | undefined): ReviewState {
  switch ((verdict ?? '').toLowerCase()) {
    case 'approve':
      return 'approved';
    case 'request_changes':
      return 'changes_requested';
    default:
      return 'none';
  }
}

/**
 * The review GATE's own verdict for a head whose every changed path is outside what the
 * reviewer reads (`no_reviewable_paths`: the repo's `path_filters` or the built-in junk
 * list — a lockfile-only Dependabot bump, a docs-only push). No model reviews such a head
 * and none ever will, so before this verdict existed `pr.require_review` waited on it
 * until it escalated `review_absent` (tesote/tesote.com#240). It is a FILED verdict with
 * a written reason — the `review.nothing_reviewable` audit row the enqueue gate writes for
 * that exact head, which `@developerz/db` `findLatestPlatformReviewVerdict` reads back —
 * never an absence read as approval. It takes no gate position
 * ({@link mapPlatformReviewVerdict} maps it to `none`, like `comment`). Stored value.
 */
export const NOTHING_REVIEWABLE_VERDICT = 'nothing_reviewable';

/**
 * Every verdict value that counts as filed: what our reviewer records
 * (`@developerz/review` `Verdict`) plus the gate's {@link NOTHING_REVIEWABLE_VERDICT}.
 */
const PLATFORM_VERDICTS: ReadonlySet<string> = new Set([
  'approve',
  'comment',
  'request_changes',
  NOTHING_REVIEWABLE_VERDICT,
]);

/**
 * Did our platform reviewer file a verdict we RECOGNIZE for this head (#1080)? The
 * question {@link mapPlatformReviewVerdict} throws away: it maps the neutral
 * `comment` and "nothing recorded" onto the same `'none'`, which is right for the
 * gate's precedence and wrong for "did a review happen at all". Feeds
 * {@link BabysitObservation.platformReviewFiled} and the merge egress's re-assertion
 * of the same rule.
 *
 * Fails CLOSED on anything unrecognized (`''`, a future verdict value, a corrupted
 * row): an unknown string is not evidence that a review happened, and treating it as
 * one would wave a merge through on a value nothing wrote deliberately.
 */
export function platformVerdictFiled(verdict: string | null | undefined): boolean {
  return PLATFORM_VERDICTS.has((verdict ?? '').toLowerCase());
}

/**
 * The recorded platform review-pass as the presence gate reads it (#1972):
 * the verdict, WHETHER a model actually read the diff (`brain_gap` — #1478's
 * disclosure fact, stamped on the `review_posted` event beside the verdict it
 * downgraded), and whether the reviewed PR was our own App's (the review task
 * envelope's `app_authored`). One read answers all three so the gate cannot
 * pair a verdict with another row's gap.
 */
export type PlatformReviewPass = {
  readonly verdict: string;
  /** `ReviewBrainGap` value when NO model read the diff; null on a model review. */
  readonly brainGap: string | null;
  /**
   * The reviewed PR was authored by our own App. Absent on a spec minted before
   * the #838 stamp — read as TRUE, the same fail-safe the runner takes
   * (`spec.app_authored ?? true`), so the two ends of the stamp cannot disagree
   * about what absence means.
   */
  readonly appAuthored: boolean;
  /**
   * The ensemble's completion on the review that filed this verdict (#2083):
   * `review_posted`'s `sampling_gap` — stamped ONLY when the ensemble ran
   * short (the `brain_gap` posture), so null covers a healthy full-strength
   * ensemble, a single-sample brain, and events predating the stamp alike.
   * All fail open; only a recorded gap can refuse below.
   */
  readonly samplesRequested: number | null;
  readonly samplesCompleted: number | null;
  /**
   * Of the samples {@link samplesRequested} counts, how many the review's own grace
   * deadline STOPPED (#3425, `apps/runner/.../review-brain/sample-grace.ts`) — the sum of
   * `grace_dropped` over the SAME run's `review_sampled` bursts. A dropped slot is the
   * platform declining to wait for a straggler once a peer answered, not a sample that
   * failed, so the sample floor credits it ({@link platformVerdictSatisfiesPresence}).
   * ABSENT when the run recorded none (every pre-#3425 run, every burst the deadline
   * never fired on, a telemetry row that never landed) — which credits nothing, the
   * fail-CLOSED direction for a number that can only ever loosen the floor. Optional
   * for that reason: a producer that forgets it makes the gate stricter, never looser.
   */
  readonly samplesGraceDropped?: number;
  /**
   * The MANDATORY passes the review that filed this verdict did NOT complete
   * (#4599): `review_posted`'s `missing_mandatory`, as
   * {@link MissingMandatoryStamp} — THREE states, because there are three facts and
   * a nullable array can only carry two of them.
   */
  readonly missingMandatory: MissingMandatoryStamp | null;
  /**
   * The PR's own text the untrusted-input screen HELD on the run that filed this
   * verdict (#6339): the field kinds and the screen's reason for each, never the
   * text — read off the SAME run's `review_pr_screen` status row and re-graded with
   * the shared `withheldFieldHolds` (the stored `pr_text_holding` count is the box's
   * own arithmetic on untrusted input, so it is never read). A non-empty list means
   * the verdict's `request_changes` was FORCED by the screen
   * (`verdictWithDiscardedPrText`), which is what lets the merge egress name the
   * refusal `screen_held_pr_text` instead of a generic `changes_requested`.
   *
   * `null` when the run recorded no screen row to read (every run predating the
   * stamp, a review whose screen call never emitted). REQUIRED and nullable: a
   * producer states "no screen row", it does not omit the question. It changes no
   * gate by itself — the `changes_requested` hold stands either way — only what the
   * hold is NAMED.
   */
  readonly prTextHeld: readonly ScreenHeldPrField[] | null;
  /**
   * The `task_run_events.id` of the `review_posted` row this verdict was read from (#7187) —
   * what a merge past GitHub's required review records as the approval it rode on. Absent
   * on the review gate's own verdict (`nothing_reviewable`, no model row) and on fixtures
   * that predate it; absent never admits that merge (`approvingVerdictEventId`), so a
   * producer that forgets it makes the gate stricter, never looser.
   */
  readonly verdictEventId?: string;
  /**
   * Set ONLY when this verdict was recorded for an EARLIER head of the PR and stands in for
   * the live one because the babysit's review-verdict carry PROVED the PR's change identical
   * on both heads (`apps/worker` `babysit/review-verdict-carry.ts`): the head it was recorded
   * for. Absent on every verdict read for the head it names — the ordinary case.
   */
  readonly carriedFromHeadSha?: string;
};

/**
 * WHAT THE ROW SAYS about the review's mandatory passes. Discriminated rather than a
 * nullable array, because "no stamp was written" and "a stamp was written and we
 * cannot read it" are different facts with OPPOSITE safe answers, and collapsing
 * them onto `null` is what made the first version of this field fail open: a box
 * emitting a malformed stamp got the same pass as a box predating the feature.
 *
 * - `{ kind: 'stamped', passes }` — the review graded its matrix. `passes` is what it
 *   left incomplete, EMPTY on a healthy review, so the gate reads content not presence.
 * - `{ kind: 'unreadable' }` — the row carries a `missing_mandatory` that is not a
 *   bounded list of kind names (`ReviewPostedPayloadSchema`). The box is untrusted
 *   input and this row steers a merge, so this REFUSES: a review whose own record we
 *   cannot parse is a review we cannot vouch for. It is deliberately not a throw —
 *   this is a read on the merge path, and an unparseable row must produce a refusal
 *   an operator can read, never an exception.
 * - `null` — NO stamp on the row at all. The one fail-open value, and the reason it
 *   is safe: every row written before the stamp shipped has none, exactly as with an
 *   absent `brain_gap` or `sampling_gap`.
 */
export type MissingMandatoryStamp =
  | { readonly kind: 'stamped'; readonly passes: readonly string[] }
  | { readonly kind: 'unreadable' };

/**
 * The unattended lane's sample-completion floor (#2083), as a RATIO:
 * `completed / requested` must reach `FLOOR / BASE` (4-of-5), from the
 * ensemble's own recall math (5 samples union to ~4x one sample's findings —
 * one lost sample is a small recall loss, two is a large one) priced against
 * the park cost: a below-floor review parks the PR as `review_absent` until a
 * re-review lands, and the only same-head re-review today is the manual
 * `review.rerun` verb. A ratio rather than a flat count because the adapter
 * AGGREGATES completion across fan-out groups, so `requested` can be N groups
 * x 5 — for `requested <= 5` the ratio is exactly `min(4, requested)` (a
 * deliberately smaller request is floored at itself, never asked to exceed
 * what the operator configured); past 5 it scales as one-lost-per-burst.
 * Consumption-side only — `DZ_REVIEW_SAMPLES` stays the request-side decision.
 */
export const REVIEW_PRESENCE_SAMPLE_FLOOR = 4;
export const REVIEW_PRESENCE_SAMPLE_BASE = 5;

/**
 * Does this recorded pass SATISFY review presence under `pr.require_review`
 * (#1972)? {@link platformVerdictFiled} answers "did our reviewer file anything
 * we recognize"; this answers the stricter question the unattended lane needs:
 * a FLOOR review — no model resolved, the deterministic changed-files summary
 * that is structurally incapable of producing a finding — files a disclosed
 * `comment` that reads nothing, and counting it as the review leg having run is
 * how a merge proceeds on CI green plus a review in name only.
 *
 * Refused on EVERY lane, whoever authored the pull request. It was once refused
 * only where `appAuthored` was true, on the premise that "a human PR's merge is a
 * human act" — and that premise is false in this codebase. Nothing in either merge
 * path is author-scoped: `driveForPr` admits any PR on a repo that opted into
 * `auto_merge` and mints a watch anchor for one no fleet task owns
 * (`apps/worker/src/babysit/driver.ts`), the anchor exists precisely to drive PRs we
 * did NOT author (`apps/worker/src/babysit/watch.ts`), `Capability.GhPrsMerge` is
 * granted off `pr.auto_merge` plus the absence of a `human_merge` lane
 * (`packages/tools/src/policy-gate.ts`), and `pr.auto_merge` / `pr.require_review`
 * both default ON. So the carve-out's own lane — a human's pull request on an
 * auto-merge repo — is exactly the one where a review that read nothing lands
 * UNREVIEWED third-party code on a repo whose maintainer asked for review.
 *
 * `appAuthored` is kept for the ONE fact it was stamped for (#838): GitHub 422s a
 * self-review, so our verdict may only post as a COMMENT on a PR our App authored
 * (`packages/review/src/finding.ts`, `submission.ts`). Reusing that flag as a
 * quality-gating lane selector put two unrelated meanings on one field, and the
 * merge hole was the cost.
 *
 * A pass with no gap recorded satisfies exactly as before — including every
 * event from a binary predating the `brain_gap` stamp, which is the fail-open
 * direction that converges as the fleet rolls; a floor review there is still
 * DISCLOSED on the PR, just not refused by this gate. Only a pass that
 * AFFIRMATIVELY recorded "no model read this" — or an ensemble that ran short —
 * is refused, so widening the lane parks no legacy row.
 *
 * What it COSTS, said out loud: the refusal parks the PR as `review_absent`, and
 * the same-head unpark is ONE automated re-review per head (below), then the manual
 * `review.rerun` verb. On an account
 * whose org BYOK pool holds no usable key, every PR now parks instead of merging
 * unreviewed. Measured on prod 2026-09-20: `developerz-ai` held 60 org keys, NONE
 * `valid` (57 revoked, 3 `credits_exhausted`), and its 122 `dispatch.no_credential`
 * audit rows since 2026-09-18 all read `{kind: 'no_pool', poolSize: 0}`. A park is
 * the correct outcome — an unreviewed merge is the incorrect one — but it is a
 * visible operational change, not a no-op.
 *
 * The second refusal is the sample-completion floor (#2083), same lane, same
 * fail-open posture on an absent stamp: a review whose ensemble completed
 * below {@link REVIEW_PRESENCE_SAMPLE_FLOOR} (floored at its own request) is
 * not the satisfying review — a 1-of-5 zero-finding pass reads "0 finding(s)"
 * exactly like a 5-of-5 one, and this stamp is the only thing downstream that
 * tells them apart. The refusal parks the PR as `review_absent` (audited);
 * the unpark is a re-review (see the automated one below; `review.rerun` by hand).
 *
 * The third is the MANDATORY PASS SET (#4599), and it is the one the other two
 * cannot stand in for. `verdictWithPassMatrix` (`@developerz/review`
 * `passes.ts`) already downgrades an APPROVE to `comment` when a mandatory pass
 * is not `completed` — so a model-backed review whose `security` reviewer FAILED
 * posts `COMMENTED` on the pull request, records `brainGap === null` (a model
 * did read the diff) and no `sampling_gap` (its samples completed), and before
 * this field existed satisfied presence and merged. The review said "I cannot
 * approve this" and the merge happened anyway. Refused on the array's CONTENT,
 * so the healthy `[]` every review now stamps still satisfies, and `null` — a
 * binary predating the stamp — still satisfies too. A stamp that is PRESENT and
 * UNREADABLE is the third state and refuses; see {@link MissingMandatoryStamp} for
 * why that is not the same fact as an absent one.
 *
 * THE ENUMERATION, so "every reason a filed verdict is not decisive" is a claim
 * with a list behind it rather than an adjective. This predicate refuses on
 * exactly five facts, each with both sides asserted in `babysit.test.ts`:
 * no pass at all (`null`/`undefined`), a verdict {@link platformVerdictFiled}
 * does not recognize, `brainGap` (#1478), the `samples*` pair below the floor
 * (#2083 — grace-dropped samples credited, {@link samplesCountedTowardFloor}),
 * and `missingMandatory` non-empty OR unreadable (#4599). The last three are
 * stamps whose ABSENCE reads as "fine" — absence only, never an unreadable presence;
 * nothing else on `PlatformReviewPass` gates.
 *
 * All three stamped refusals park the PR as `review_absent` and take the SAME
 * remedy — a re-review. No fourth `BabysitMergeRefusalCode` is warranted:
 * the code names the FIX, and a second one for the same fix would orphan the
 * `review_absent` branch that mints the unpark. WHICH pass was missing is named
 * in the refusal's DETAIL string (`merge-gate/guarded-merge.ts`). The automated
 * unpark is the SAME for all three: the `review_absent` escalation asks
 * `reReviewDegradedHead` (`apps/worker/src/review/command-enqueue.ts`) for ONE
 * re-review of the head, deduped per head, keyed on
 * {@link platformReviewDegradation} — which reads THIS reduction, so "why our
 * review did not count" and "whether a re-review is owed" cannot disagree.
 */
export function platformVerdictSatisfiesPresence(
  pass: PlatformReviewPass | null | undefined,
): boolean {
  return presenceRefusal(pass) === null;
}

/**
 * Why OUR filed review stands as a review yet does not satisfy presence — the three
 * STAMPED refusals of {@link platformVerdictSatisfiesPresence}'s enumeration, each one
 * a review that ran degraded and that a re-review of the same head can converge.
 */
export type PlatformReviewDegradation = 'brain_gap' | 'missing_mandatory' | 'sampling_gap';

/**
 * The stamped reason our filed verdict failed presence, or `null` when it satisfied
 * presence OR no usable verdict was filed at all (no pass, an unrecognized verdict) —
 * those two are "nothing of ours to re-run", not a degraded review.
 *
 * Measured on prod 2026-09-25: tesote/tesote.ai#2624 head e548d01 filed `comment`
 * with `sampling_gap {requested: 2, completed: 0}` (two concern reviewers lost their
 * only sample to a schema-invalid tool response), parked `review_absent` on a green,
 * CLEAN pull request for 130 min, escalated, and was merged by hand — because the
 * automated re-review only ever fired for a `brain_gap`.
 */
export function platformReviewDegradation(
  pass: PlatformReviewPass | null | undefined,
): PlatformReviewDegradation | null {
  const refusal = presenceRefusal(pass);
  return refusal === 'no_verdict' ? null : refusal;
}

/**
 * The two observation fields derived from OUR verdict row (#6339): the degradation
 * above, and the screen-held PR-text fields that ride beside the verdict that forced
 * them — the hold's wait reason, the hold-status comment and the merge egress's
 * `screen_held_pr_text` all read ONE answer. One mapping so the driver's observation
 * literal stays at the size ratchet's pin.
 */
export function platformReviewFields(pass: PlatformReviewPass | null | undefined): {
  readonly platformReviewDegradation: PlatformReviewDegradation | null;
  readonly prTextHeld: readonly ScreenHeldPrField[] | null;
} {
  return {
    platformReviewDegradation: platformReviewDegradation(pass),
    prTextHeld: pass?.prTextHeld ?? null,
  };
}

/** The ONE reduction both exports above read. `no_verdict` covers no pass and an unrecognized verdict. */
function presenceRefusal(
  pass: PlatformReviewPass | null | undefined,
): PlatformReviewDegradation | 'no_verdict' | null {
  if (pass === null || pass === undefined) return 'no_verdict';
  if (!platformVerdictFiled(pass.verdict)) return 'no_verdict';
  if (pass.brainGap !== null) return 'brain_gap';
  // #4599: three states, not two. `null` (no stamp) satisfies; a stamp we cannot read
  // refuses; a stamp we CAN read refuses on its CONTENT, so the healthy `[]` satisfies.
  if (pass.missingMandatory !== null) {
    if (pass.missingMandatory.kind === 'unreadable') return 'missing_mandatory';
    if (pass.missingMandatory.passes.length > 0) return 'missing_mandatory';
  }
  if (pass.samplesRequested !== null && pass.samplesCompleted !== null) {
    // Integer ratio compare — no floats, no rounding decision to get wrong.
    if (
      samplesCountedTowardFloor(pass) * REVIEW_PRESENCE_SAMPLE_BASE <
      pass.samplesRequested * REVIEW_PRESENCE_SAMPLE_FLOOR
    ) {
      return 'sampling_gap';
    }
  }
  return null;
}

/**
 * The samples the floor counts: the ones that COMPLETED plus the ones the review's own
 * grace deadline stopped (#3425), capped at the request.
 *
 * WHY A GRACE DROP IS NOT A LOST SAMPLE. The floor (#2083) exists to catch an ensemble
 * that FAILED — samples a provider refused, truncated or never answered — because that
 * review is weaker than it looks and a re-review can converge it. A grace drop is none
 * of those: the burst already had an answer and the platform chose to stop waiting,
 * disclosed on the review body. Counted as lost, the platform's own latency bound parked
 * the platform's own PRs: on a model slower than the window EVERY review drops, so the
 * park's only exit — a re-review, a model call on the same model — drops again. Measured
 * on prod 2026-09-24: tesote/tesote.ai#2584 posted 5-of-9 with 0 failures, 4 grace drops
 * and 0 findings, sat `review_absent` on a green PR for two hours, escalated, and was
 * merged by hand. The recall lever is `DZ_REVIEW_SAMPLE_GRACE_MS`, never a park.
 *
 * Credited only beside at least one completed sample — a drop exists only after a peer
 * answered (`settleWithGrace` arms on the first fulfilment), so drops beside none are a
 * stamp describing the impossible and earn nothing — and capped at the request, so an
 * over-reported count cannot lift the ratio past full completion.
 */
function samplesCountedTowardFloor(pass: PlatformReviewPass): number {
  const requested = pass.samplesRequested ?? 0;
  const completed = pass.samplesCompleted ?? 0;
  const dropped = completed > 0 ? (pass.samplesGraceDropped ?? 0) : 0;
  return Math.min(requested, completed + dropped);
}

/** Map GitHub's `mergeable` enum to our tri-state (anything non-definitive ⇒ `unknown`). */
export function mapMergeable(mergeable: string | null | undefined): Mergeability {
  switch ((mergeable ?? '').toUpperCase()) {
    case 'MERGEABLE':
      return 'mergeable';
    case 'CONFLICTING':
      return 'conflicting';
    default:
      return 'unknown';
  }
}

/** GitHub check conclusions/states that mean FAILED (mirrors monitor's `CHECK_FAILURE`). */
const CHECK_FAILURE = new Set([
  'FAILURE',
  'CANCELLED',
  'TIMED_OUT',
  'ACTION_REQUIRED',
  'STARTUP_FAILURE',
  'ERROR',
  'STALE',
]);
/** Conclusions/states that mean PASSED (or a benign non-failure). */
const CHECK_SUCCESS = new Set(['SUCCESS', 'NEUTRAL', 'SKIPPED']);

/**
 * A conclusion {@link gradeCheck} reads as PASSING that is no verdict on the code — the
 * check declined to judge (#5675). Right as passing for the merge gate, which asks "is
 * anything failing" (GitHub counts a skipped required check as satisfied), wrong as proof
 * the head is green: a lone skipped job read whole PRs green (infrastructure#1605,
 * wurk#527). The ONE predicate the PR projection's webhook fold (`apps/worker`
 * `check-fold-grade.ts`) and the live read (`@developerz/review` `fetchChecks`) share.
 */
export function isNoVerdictConclusion(conclusion: string | null | undefined): boolean {
  const outcome = (conclusion ?? '').toUpperCase();
  return outcome === 'SKIPPED' || outcome === 'NEUTRAL';
}

/**
 * The `pull_requests.ci_failing_checks` key the PR-projection reconciler writes for a
 * check its live read saw failing (#5675, `@developerz/db` `applyPrLiveCiState`), and the
 * key the webhook fold clears (`applyPrCheckState`) — ONE spelling for both writers. The
 * live read carries no app, so it cannot mint the fold's `run:<app>:<name>`: it is keyed
 * by NAME, and the fold's pass or neutral of ANY same-named check run clears it — a commit
 * status's key included, since the live read keys a status by its bare `context` (the
 * accepted name-collision class, as the fold's `checkKeyOf` WARN 5). A status no check run
 * shares a name with has no fold path (no `status` event folds): its key clears only on
 * the reconciler's next visit of the open row or a head move. Pure, so it lives here
 * rather than in `@developerz/db`, whose root exports take a handle.
 */
export function liveCheckKey(name: string): string {
  return `live:${name}`;
}

/**
 * Grade one check entry (a check run or a status context). `CANCELLED` grades `failing`
 * here, for every reader: only the babysit observation refines it to `cancelled`, off
 * the check's attempt history (`CheckConclusion`).
 */
export function gradeCheck(entry: {
  status?: string | undefined;
  conclusion?: string | null | undefined;
  state?: string | undefined;
}): CheckGrade {
  const status = (entry.status ?? '').toUpperCase();
  if (status !== '' && status !== 'COMPLETED') return 'pending';
  const outcome = (entry.conclusion ?? entry.state ?? '').toUpperCase();
  if (CHECK_FAILURE.has(outcome)) return 'failing';
  if (CHECK_SUCCESS.has(outcome)) return 'passing';
  return 'pending'; // PENDING / EXPECTED / QUEUED / unknown → in-flight
}

/**
 * One PR review THREAD as the observe seam read it off GraphQL `reviewThreads` (#3884) —
 * the thread's own resolution plus its ROOT comment (author, id, anchor, body). REST
 * `/pulls/<n>/comments` cannot serve here: it returns resolved and outdated comments alike
 * and carries no resolution field, which is how a fixed finding kept being re-dispatched.
 */
export type RawReviewThread = {
  /** The thread's GraphQL node id (`PRRT_…`). */
  readonly threadId: string;
  readonly isResolved: boolean;
  /** The root comment's REST `databaseId` — the id space every envelope field uses. */
  readonly rootCommentId: number;
  /** The root comment's author login; GraphQL spells an App without `[bot]`. `null` = ghost. */
  readonly author: string | null;
  readonly path: string | null;
  readonly line: number | null;
  /** The root comment's `originalCommit.oid` — 40 hex, or null when GitHub gave none. */
  readonly originalCommitSha: string | null;
  readonly body: string;
  /** Our fixer's newest answer here is a decline the reviewer's re-check has not ruled on — {@link PrFinding.declineAwaitingRecheck}. */
  readonly declineAwaitingRecheck: boolean;
};

/**
 * The one place a bot login's `[bot]` suffix is stripped (11-2): trim, case-fold, strip,
 * so a bot's REST/webhook spelling (`coderabbitai[bot]`) and its GraphQL spelling
 * (`coderabbitai`) compare equal. It says nothing about human logins, and it is NOT the
 * seam for "same account": several login comparisons still case-fold by hand.
 */
export function botLoginKey(login: string): string {
  const lower = login.trim().toLowerCase();
  return lower.endsWith('[bot]') ? lower.slice(0, -'[bot]'.length) : lower;
}

/**
 * Distil the PR's review threads into the findings a `fix_pr` round answers, for ONE
 * reviewer identity (#3884, #4260) — the observe seam calls it once per reviewer:
 *
 *   - `ours` — every UNRESOLVED thread our bot rooted, whatever its severity, outdated ones
 *     included (an outdated thread of ours still has to be answered). `blocking` is the
 *     rendered `🚫` prefix.
 *   - `coderabbit` — every UNRESOLVED thread the incumbent reviewer rooted whose severity is
 *     `major`+ OR does not parse: exactly the threads the merge gate refuses on
 *     (`@developerz/review` `merge-gate/findings.ts` `findingHold`, same parser). Every one
 *     holds the merge, so every one is `blocking`. Without them decide prescribed a merge
 *     the gate refused on every pass. The round FIXES the code; the harness never replies
 *     to or resolves these (no bot-on-bot loop, {@link PrFinding.reviewer}).
 *
 * A resolved thread has taken its exit and is not a finding. `reviewerLogin` is compared
 * with `[bot]` stripped and case folded, so a REST and a GraphQL spelling agree.
 * Pure — the observe builder does the GraphQL read, this maps the shape.
 */
export function distillThreadFindings(
  threads: readonly RawReviewThread[],
  reviewerLogin: string,
  reviewer: PrFinding['reviewer'],
): PrFinding[] {
  const wanted = botLoginKey(reviewerLogin);
  const findings: PrFinding[] = [];
  for (const t of threads) {
    if (t.isResolved) continue;
    if (t.author === null || botLoginKey(t.author) !== wanted) continue;
    const severity = parseFindingSeverity(t.body);
    if (reviewer === 'coderabbit' && !incumbentHoldsMerge(severity)) continue;
    findings.push({
      id: t.rootCommentId,
      reviewer,
      threadId: t.threadId,
      file: t.path,
      line: t.line,
      severity,
      originalCommitSha: t.originalCommitSha,
      blocking: reviewer === 'coderabbit' || findingBodyIsBlocking(t.body),
      body: t.body,
      // The harness never replies on an incumbent's thread, so no decline of ours can sit there.
      declineAwaitingRecheck: reviewer === 'ours' && t.declineAwaitingRecheck,
    });
  }
  return findings;
}

/** The merge gate's incumbent rule: `major`+ holds, and an unreadable tag is drift, which holds too. */
function incumbentHoldsMerge(severity: FindingSeverity | null): boolean {
  return severity === null || FINDING_SEVERITY_RANK[severity] >= FINDING_SEVERITY_RANK.major;
}
